import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';
import { SignaturePad } from '../components/SignaturePad';
import { useToast } from '../context/ToastContext';
import './Pages.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export default function PublicFormPage() {
  const { token } = useParams<{ token: string }>();
  const { showToast } = useToast();
  const [formData, setFormData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Client response fields
  const [response, setResponse] = useState<Record<string, any>>({});
  const [clientSignature, setClientSignature] = useState('');

  useEffect(() => {
    axios.get(`${API}/forms/${token}`)
      .then(res => {
        setFormData(res.data);
        // Pre-fill with any previous client_response
        if (res.data.client_response) {
          setResponse(res.data.client_response);
          setClientSignature(res.data.client_response.signature || '');
        }
      })
      .catch(err => {
        if (err.response?.status === 410) setError('Dieser Link ist abgelaufen.');
        else setError('Formular konnte nicht geladen werden.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/forms/${token}`, {
        ...response,
        signature: clientSignature,
        submitted_at: new Date().toISOString(),
      });
      setSubmitted(true);
    } catch {
      showToast('Senden fehlgeschlagen. Bitte versuchen Sie es erneut.', 'error');
    }
    setSubmitting(false);
  };

  const handleChange = (name: string, value: string | number) => {
    setResponse(prev => ({ ...prev, [name]: value }));
  };

  // Helper for textarea
  const renderTextarea = (name: string, label: string, placeholder: string) => (
    <div className="form-group">
      <label>{label}</label>
      <textarea
        value={response[name] || ''}
        onChange={(e) => handleChange(name, e.target.value)}
        placeholder={placeholder}
        style={{ minHeight: 80 }}
      />
    </div>
  );

  // Helper for range/scale
  const renderScale = (name: string, label: string) => (
    <div className="form-group">
      <label>{label}</label>
      <div className="range-display">
        <input
          type="range"
          min={1}
          max={10}
          value={response[name] || 5}
          onChange={(e) => handleChange(name, Number(e.target.value))}
          style={{ flex: 1 }}
        />
        <span className="range-value">{response[name] || 5}</span>
      </div>
    </div>
  );

  // Render form fields per module_type
  const renderFormFields = () => {
    if (!formData) return null;

    switch (formData.module_type) {
      case 'erstgespraech':
        return (
          <div className="block-form-section">
            <h4>Ihre Angaben</h4>
            {renderTextarea('anliegen', 'Anliegen', 'Was führt Sie zum Coaching?')}
            {renderTextarea('erwartungen', 'Erwartungen', 'Was erwarten Sie sich vom Coaching?')}
            {renderTextarea('rahmenbedingungen', 'Rahmenbedingungen', 'Gibt es Rahmenbedingungen, die wir berücksichtigen sollten?')}
          </div>
        );

      case 'anamnese':
        return (
          <div className="block-form-section">
            <h4>Anamnese</h4>
            {renderTextarea('berufliche_situation', 'Berufliche Situation', 'Beschreiben Sie Ihre aktuelle berufliche Situation.')}
            {renderTextarea('aktuelle_herausforderungen', 'Aktuelle Herausforderungen', 'Welche Herausforderungen beschäftigen Sie derzeit?')}
            {renderTextarea('bisherige_loesungsversuche', 'Bisherige Lösungsversuche', 'Was haben Sie bisher versucht, um die Situation zu verbessern?')}
            {renderTextarea('ressourcen', 'Ressourcen', 'Welche Ressourcen und Stärken bringen Sie mit?')}
          </div>
        );

      case 'zieldefinition':
        return (
          <div className="block-form-section">
            <h4>Zieldefinition</h4>
            {formData.content_data?.ziele && (
              <div className="form-group">
                <label>Ziele des Coaches (zur Information)</label>
                <div style={{
                  background: '#faf7f3',
                  borderRadius: 8,
                  padding: '12px 16px',
                  fontSize: '0.9rem',
                  color: '#666',
                  whiteSpace: 'pre-wrap',
                  marginBottom: 12,
                }}>
                  {typeof formData.content_data.ziele === 'string'
                    ? formData.content_data.ziele
                    : Array.isArray(formData.content_data.ziele)
                      ? formData.content_data.ziele.map((z: any, i: number) => (
                          <div key={i} style={{ marginBottom: 8 }}>
                            {typeof z === 'string' ? z : z.beschreibung || z.ziel || JSON.stringify(z)}
                          </div>
                        ))
                      : JSON.stringify(formData.content_data.ziele)}
                </div>
              </div>
            )}
            {renderTextarea('kommentar', 'Ihr Kommentar', 'Ihre Anmerkungen zu den Zielen.')}
          </div>
        );

      case 'session':
        return (
          <div className="block-form-section">
            <h4>Sitzungs-Feedback</h4>
            {renderTextarea('befinden_vor', 'Befinden vor der Sitzung', 'Wie geht es Ihnen vor der Sitzung?')}
            {renderScale('befinden_skala', 'Befinden-Skala (1 = sehr schlecht, 10 = sehr gut)')}
            {renderTextarea('feedback', 'Feedback zur Sitzung', 'Wie haben Sie die Sitzung erlebt?')}
          </div>
        );

      case 'reflexion':
        return (
          <div className="block-form-section">
            <h4>Reflexion</h4>
            {renderScale('zufriedenheit', 'Zufriedenheit (1 = sehr unzufrieden, 10 = sehr zufrieden)')}
            {renderTextarea('gut_funktioniert', 'Was hat gut funktioniert?', 'Was lief besonders gut?')}
            {renderTextarea('besser_sein', 'Was könnte besser sein?', 'Was würden Sie sich anders wünschen?')}
            {renderScale('fortschritt_ziele', 'Fortschritt bei den Zielen (1 = kein Fortschritt, 10 = Ziel erreicht)')}
            {renderScale('beziehung_coach', 'Beziehung zum Coach (1 = schwierig, 10 = sehr vertrauensvoll)')}
            {renderTextarea('anmerkungen', 'Anmerkungen', 'Weitere Anmerkungen oder Wünsche.')}
          </div>
        );

      case 'abschluss':
        return (
          <div className="block-form-section">
            <h4>Abschluss</h4>
            {renderTextarea('key_learnings', 'Key Learnings', 'Was nehmen Sie aus dem Coaching mit?')}
            {renderScale('gesamtbewertung', 'Gesamtbewertung (1 = schlecht, 10 = hervorragend)')}
            {renderTextarea('abschluss_kommentar', 'Abschluss-Kommentar', 'Möchten Sie noch etwas mitteilen?')}
          </div>
        );

      default:
        return (
          <div className="block-form-section">
            <h4>Ihre Angaben</h4>
            {renderTextarea('kommentar', 'Kommentar', 'Ihre Anmerkungen.')}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="public-form-page">
        <div className="public-form-container">
          <p>Wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-form-page">
        <div className="public-form-container">
          <div className="public-form-header">
            <h1>CoachDesk365</h1>
          </div>
          <div className="public-form-body">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="public-form-page">
        <div className="public-form-container">
          <div className="public-form-header">
            <h1>CoachDesk365</h1>
          </div>
          <div className="public-form-body public-form-success">
            <CheckCircle2 size={48} color="#2E8B57" />
            <h2>Vielen Dank!</h2>
            <p>Ihre Antworten wurden erfolgreich gesendet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-form-page">
      <div className="public-form-container">
        <div className="public-form-header">
          <h1>CoachDesk365</h1>
          <p>{formData.coach_name}</p>
        </div>
        <div className="public-form-body">
          <div className="public-form-info">
            <strong>{formData.block_label}</strong>
            {formData.case_title && <span> — {formData.case_title}</span>}
          </div>

          {renderFormFields()}

          <div style={{ marginTop: 24 }}>
            <SignaturePad
              label="Ihre Unterschrift"
              value={clientSignature}
              onChange={setClientSignature}
              height={150}
            />
          </div>

          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
              style={{ padding: '14px 48px', fontSize: '1rem' }}
            >
              {submitting ? 'Wird gesendet...' : 'Absenden'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
